<template>
  <div class="page-shell">
    <div class="topbar">
      <div class="page-title">
        <h1>📊 数据看板</h1>
        <p>COG主播平台运营数据总览</p>
      </div>
    </div>

    <div class="stat-cards">
      <div class="stat-card"><div class="sc-icon blue">👤</div><div class="sc-info"><div class="sc-num">{{ totalUsers }}</div><div class="sc-label">平台用户总数</div></div></div>
      <div class="stat-card"><div class="sc-icon cyan">🎙️</div><div class="sc-info"><div class="sc-num">{{ totalAnchors }}</div><div class="sc-label">主播总数</div></div></div>
      <div class="stat-card"><div class="sc-icon gold">📝</div><div class="sc-info"><div class="sc-num">{{ pendingApps }}</div><div class="sc-label">待处理申请</div></div></div>
      <div class="stat-card"><div class="sc-icon green">✅</div><div class="sc-info"><div class="sc-num">{{ totalApps }}</div><div class="sc-label">申请总数</div></div></div>
    </div>

    <div class="chart-row">
      <div class="card">
        <div class="card-header"><h3>用户类型分布</h3></div>
        <div class="overview-boxes">
          <div class="overview-box"><div class="overview-num primary">{{ anchorUsers }}</div><div class="overview-label">个人主播用户</div></div>
          <div class="overview-box"><div class="overview-num accent">{{ mcnUsers }}</div><div class="overview-label">MCN机构用户</div></div>
        </div>
      </div>
      <div class="card">
        <div class="card-header"><h3>主播类型分布</h3></div>
        <div class="overview-boxes">
          <div class="overview-box"><div class="overview-num primary">{{ personalAnchors }}人</div><div class="overview-label">个人主播</div></div>
          <div class="overview-box"><div class="overview-num accent">{{ orgAnchors }}人</div><div class="overview-label">机构主播</div></div>
        </div>
      </div>
    </div>

    <div class="card">
      <div class="card-header"><h3>📋 最近申请记录</h3></div>
      <div class="table-wrap">
        <table>
          <thead>
            <tr><th>申请编号</th><th>类型</th><th>申请人</th><th>提交时间</th><th>状态</th><th>操作</th></tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="6" class="table-empty">加载中...</td>
            </tr>
            <tr v-else-if="!recentApps.length">
              <td colspan="6" class="table-empty">暂无申请记录</td>
            </tr>
            <template v-else>
              <tr v-for="item in recentApps" :key="`${item.type}-${item.id}`">
                <td style="color:var(--cog-primary);font-weight:600">{{ item.no }}</td>
                <td>{{ typeText(item.type) }}</td>
                <td>{{ item.applicant }}</td>
                <td>{{ item.createdAt }}</td>
                <td><span :class="statusClass(item.status)">{{ item.statusText }}</span></td>
                <td><button class="btn btn-sm btn-outline" @click="goApplication(item)">查看</button></td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { dashboardStats } from '../../api/userMG'

const createDefaultTotals = () => ({
  platform_users: 0,
  individual_users: 0,
  mcn_users: 0,
  streamers: 0,
  individual_streamers: 0,
  mcn_streamers: 0,
  pending_applications: 0,
  total_applications: 0
})

export default {
  name: 'dashboard',
  data() {
    return {
      loading: false,
      totals: createDefaultTotals(),
      recentApplications: []
    }
  },
  computed: {
    totalUsers() { return this.totals.platform_users },
    totalAnchors() { return this.totals.streamers },
    pendingApps() { return this.totals.pending_applications },
    totalApps() { return this.totals.total_applications },
    anchorUsers() { return this.totals.individual_users },
    mcnUsers() { return this.totals.mcn_users },
    personalAnchors() { return this.totals.individual_streamers },
    orgAnchors() { return this.totals.mcn_streamers },
    recentApps() { return this.recentApplications }
  },
  created() {
    this.loadDashboard()
  },
  methods: {
    async loadDashboard() {
      this.loading = true

      try {
        const res = await dashboardStats()

        if (!this.isDashboardSuccess(res)) {
          this.$message.error(this.getErrorMessage(res, '数据看板加载失败'))
          this.totals = createDefaultTotals()
          this.recentApplications = []
          return
        }

        const payload = res && res.data ? res.data : {}

        this.totals = Object.assign(createDefaultTotals(), payload.totals || {})
        this.recentApplications = Array.isArray(payload.recent_applications)
          ? payload.recent_applications.map(item => ({
            id: item.id,
            type: item.type,
            no: item.no,
            applicant: item.applicant,
            status: item.status,
            statusText: item.status_text || this.fallbackStatusText(item.status),
            createdAt: item.created_at
          }))
          : []
      } catch (error) {
        this.$message.error(this.getErrorMessage(error, '数据看板加载失败'))
      } finally {
        this.loading = false
      }
    },
    isDashboardSuccess(res) {
      return !!(res && typeof res === 'object' && res.data && !res.message && !res.detail && !res.errors)
    },
    typeText(type) {
      return {
        cooperation_application: '商业合作',
        asset_evaluation: '资产评估'
      }[type] || type
    },
    fallbackStatusText(status) {
      return {
        pending: '待处理',
        processed: '已处理',
        approved: '已通过',
        rejected: '已驳回'
      }[status] || status
    },
    statusClass(status) {
      return {
        status: true,
        'status-pending': status === 'pending',
        'status-approved': status === 'processed' || status === 'approved',
        'status-rejected': status === 'rejected'
      }
    },
    getErrorMessage(error, fallback) {
      if (!error) return fallback
      if (typeof error === 'string') return error
      if (error.message) return error.message
      if (error.detail) return error.detail
      if (error.errors) {
        const firstKey = Object.keys(error.errors)[0]
        const firstError = firstKey ? error.errors[firstKey] : ''

        if (Array.isArray(firstError)) {
          return firstError[0] || fallback
        }

        return firstError || fallback
      }

      return fallback
    },
    goApplication(item) {
      const routeMap = {
        cooperation_application: `/applications/business/${item.id}`,
        asset_evaluation: `/applications/evaluation/${item.id}`
      }

      const targetPath = routeMap[item.type]

      if (!targetPath) {
        this.$message.error('暂不支持该申请类型的跳转')
        return
      }

      this.$router.push(targetPath)
    }
  }
}
</script>

<style scoped>
.overview-boxes {
  display: flex;
  gap: 24px;
  justify-content: center;
  padding: 20px;
}

.overview-box {
  text-align: center;
}

.overview-num {
  font-size: 32px;
  font-weight: 800;
}

.overview-num.primary {
  color: var(--cog-primary);
}

.overview-num.accent {
  color: var(--cog-accent);
}

.overview-label {
  margin-top: 6px;
  font-size: 13px;
  color: var(--cog-text-secondary);
}

.table-empty {
  padding: 24px 12px;
  text-align: center;
  color: var(--cog-text-secondary);
}
</style>



// WEBPACK FOOTER //
// src/views/charts/statistics.vue